var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { add, addDays, addMonths, addWeeks, addYears, eachDayOfInterval, endOfMonth, endOfYear, format, getMonth, getYear, isAfter, isBefore, isSameMonth, isWithinInterval, parse, set, startOfMonth, startOfYear, sub, } from 'date-fns';
import { getParks } from '../../../utils/parks/getters';
import { HolidayType } from '../../../utils/parks/types';
export function getMonthInfo(year, month, startDay = 'monday') {
    const weeks = [];
    const firstDate = new Date(year, month, 1);
    const lastDate = new Date(year, month + 1, 0);
    const numDays = lastDate.getDate();
    let start = 1;
    let end = firstDate.getDay() === 0 ? 1 : 7 - firstDate.getDay() + 1;
    if (startDay === 'sunday') {
        end = 7 - firstDate.getDay();
    }
    while (start <= numDays) {
        weeks.push({ start, days: end - start + 1 });
        start = end + 1;
        end += 7;
        end = start === 1 && end === 8 ? 1 : end;
        if (end > numDays) {
            end = numDays;
        }
    }
    return { totalWeek: weeks, totalDay: numDays };
}
const getArrWeekDay = (weekdayFormat = 'dddd') => {
    switch (weekdayFormat) {
        case 'dd':
            return ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
        case 'ddd':
            return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        default:
            return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    }
};
export function getWeekDay(startWeekDay = 'monday', weekDayFormat = 'dddd') {
    const arrWeekDay = getArrWeekDay(weekDayFormat);
    if (startWeekDay === 'sunday') {
        const last = arrWeekDay.pop();
        arrWeekDay.unshift(last);
    }
    return arrWeekDay;
}
export const getDay = (date) => format(date, 'EE');
const toDay = () => new Date();
export const isCurrentMonth = (year, month) => toDay().getFullYear() === year && toDay().getMonth() === month;
export const isClosedMonth = (year, month, seasons) => {
    if (!(seasons === null || seasons === void 0 ? void 0 : seasons.length))
        return false;
    const currentSeason = seasons === null || seasons === void 0 ? void 0 : seasons.find((season) => getYear(season.start) === year);
    if (!currentSeason)
        return true;
    if (isCurrentMonth(year, month) && month === getMonth(currentSeason.end)) {
        return toDay() > new Date(currentSeason.end);
    }
    const monthIsInSeason = isWithinInterval(new Date(year, month, 1), {
        start: sub(startOfMonth(currentSeason.start), { days: 1 }),
        end: add(currentSeason.end, { days: 1 }),
    });
    return !monthIsInSeason;
};
export const offSeasonsDays = (seasons) => {
    return ((seasons === null || seasons === void 0 ? void 0 : seasons.reduce((prev, curr, index) => {
        const daysLeadingUpToFirstSeason = index === 0
            ? eachDayOfInterval({
                start: startOfYear(curr.start),
                end: sub(curr.start, { days: 1 }),
            })
            : [];
        if (seasons.length - 1 === index) {
            const days = eachDayOfInterval({
                start: add(curr.end, { days: 1 }),
                end: sub(endOfYear(curr.end), { days: 1 }),
            });
            return [...prev, ...daysLeadingUpToFirstSeason, ...days];
        }
        const nextSeason = seasons[index + 1];
        const days = eachDayOfInterval({
            start: add(curr.end, { days: 1 }),
            end: sub(nextSeason.start, { days: 1 }),
        });
        return [...prev, ...daysLeadingUpToFirstSeason, ...days];
    }, [])) || []);
};
export const getLastPossibleMonth = (seasons) => {
    if ((seasons === null || seasons === void 0 ? void 0 : seasons.length) === 0) {
        // Add 1 year 3 months and then get the end of that year.
        // Reason is because new years inventory normally comes out around November/December
        return endOfYear(addMonths(addYears(new Date(), 1), 3));
    }
    return seasons === null || seasons === void 0 ? void 0 : seasons.reduce((prev, next) => {
        if (isAfter(next.end, prev)) {
            return endOfMonth(next.end);
        }
        return prev;
    }, new Date());
};
export const getDisableDays = (duration) => {
    switch (duration) {
        case 3:
            return ['Mon', 'Tue', 'Wed', 'Thu', 'Sat', 'Sun'];
        case 4:
            return ['Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        case 7:
            return ['Tue', 'Wed', 'Thu', 'Sun'];
        case 10:
            return ['Mon', 'Tue', 'Wed', 'Thu', 'Sat', 'Sun'];
        case 14:
            return ['Tue', 'Wed', 'Thu', 'Sun'];
        default:
            return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    }
};
export const getEndSeasonDisabledDates = (duration, seasons) => {
    if (!seasons || !seasons.length || !duration)
        return [];
    return seasons
        .map((season) => {
        const endOfSeason = season.end;
        const firstDisabledDate = new Date(endOfSeason);
        firstDisabledDate.setDate(firstDisabledDate.getDate() - duration + 1);
        return eachDayOfInterval({
            start: firstDisabledDate,
            end: endOfSeason,
        });
    })
        .flat();
};
export const getAllDatesWithinRange = (startDate, endDate, offsetStart = 0) => {
    const offsetStartDate = add(startDate, { days: offsetStart });
    if (offsetStartDate >= endDate)
        return [];
    return eachDayOfInterval({
        start: offsetStartDate,
        end: endDate,
    });
};
export const getArrayMonth = (date, singleCalendar, offset = 1) => {
    const lastMonthIndexInView = singleCalendar ? 0 : 1;
    const array = [];
    for (let i = -offset; i <= lastMonthIndexInView + offset; i++) {
        array.push(addMonths(date, i));
    }
    return array;
};
const removePastSeasons = (seasons) => {
    const currentDate = new Date();
    return seasons
        .filter(({ start, end }) => start instanceof Date && end instanceof Date && start <= end)
        .filter(({ end }) => end >= currentDate);
};
export const formatSeasonsByYear = (seasons) => {
    const seasonsWithoutPastDates = removePastSeasons(seasons);
    const resultByYear = [];
    seasonsWithoutPastDates.forEach((season) => {
        const startYear = season.start.getFullYear().toString();
        let existingYearObject = resultByYear.find((obj) => obj.year === startYear);
        if (!existingYearObject) {
            existingYearObject = {
                year: startYear,
                start: season.start,
                end: season.end,
            };
            resultByYear.push(existingYearObject);
        }
        else {
            if (season.start < existingYearObject.start) {
                existingYearObject.start = season.start;
            }
            if (season.end > existingYearObject.end) {
                existingYearObject.end = season.end;
            }
        }
    });
    return resultByYear
        .sort((a, b) => Number(a.year) - Number(b.year))
        .map((a) => {
        delete a.year;
        return a;
    });
};
export const getSeasonOpenings = (parkCodes, defaultSeasons, service = HolidayType.HOLIDAY) => {
    var _a;
    if ((parkCodes === null || parkCodes === void 0 ? void 0 : parkCodes.length) >= 1) {
        const parks = getParks().filter(({ code }) => parkCodes.includes(code));
        const dateRanges = parks
            .map((park) => {
            const relevantHolidayType = park[service];
            if (!relevantHolidayType ||
                !relevantHolidayType.available ||
                !relevantHolidayType.seasons) {
                return [];
            }
            return relevantHolidayType.seasons.map(({ start, end }) => ({
                start: new Date(start),
                end: new Date(end),
            }));
        })
            .filter((x) => x)
            .flat();
        return (_a = formatSeasonsByYear(dateRanges)) !== null && _a !== void 0 ? _a : defaultSeasons;
    }
    return defaultSeasons;
};
export const getLowestPriceInMonth = (month, leadingPrices) => {
    if (!month || !leadingPrices.length)
        return undefined;
    const monthDate = parse(month, 'yyyy-MM', new Date());
    const leadingPricesInMonth = leadingPrices.filter((leadingPrice) => {
        const leadingPriceDate = parse(leadingPrice.date, 'dd/MM/yyyy', new Date());
        return isSameMonth(leadingPriceDate, monthDate);
    });
    if (!leadingPricesInMonth.length)
        return undefined;
    const leadingPricesWithoutNull = leadingPricesInMonth
        .filter(({ price }) => price && price > 0)
        .map(({ price }) => price);
    return Math.min(...leadingPricesWithoutNull);
};
export const fetchInventoryData = ({ inventoryDataUrl, queryParams, }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield fetch(`${inventoryDataUrl}?${queryParams}`, {
            credentials: 'include',
        });
        const data = yield response.json();
        if (data.error) {
            throw data.error;
        }
        else {
            const { resultLeadingPrices, resultAvailability, resultSeasons } = data;
            const leadingPrices = resultLeadingPrices.filter((leadingPrice) => leadingPrice.price !== null && leadingPrice.price !== 0);
            return { leadingPrices, availability: resultAvailability, seasons: resultSeasons };
        }
    }
    catch (error) {
        return { error };
    }
});
export const fetchPromotionData = ({ promotionsDataUrl, queryParams, }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield fetch(`${promotionsDataUrl}?${queryParams}`);
        const data = yield response.json();
        if (response.status !== 200) {
            throw data.message;
        }
        else {
            return data;
        }
    }
    catch (error) {
        return [];
    }
});
export const getNextAvailableDateInSeason = (seasons, fromDate = new Date()) => {
    var _a;
    const currentSeason = seasons === null || seasons === void 0 ? void 0 : seasons.filter(({ start, end }) => fromDate > start && fromDate < end);
    if (currentSeason === null || currentSeason === void 0 ? void 0 : currentSeason.length) {
        return fromDate;
    }
    const nextAvailableDate = (_a = seasons === null || seasons === void 0 ? void 0 : seasons.find(({ start }) => start > fromDate)) === null || _a === void 0 ? void 0 : _a.start;
    return nextAvailableDate || fromDate;
};
export const getMinDateAfterMidDayToday = (minDate) => {
    const isBeforeMidDay = isBefore(minDate, set(minDate, { hours: 12, minutes: 0, seconds: 0, milliseconds: 0 }));
    const minDateAtDayStart = set(minDate, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
    return isBeforeMidDay ? minDateAtDayStart : addDays(minDateAtDayStart, 1);
};
export const findPromotedCategoryByActiveId = (promotedBreaks, activePromotionId) => {
    return promotedBreaks.find((promotion) => promotion.events.some((event) => event.id === activePromotionId));
};
export const findPromotedCategoryId = (promotedBreaks, categoryId) => {
    var _a;
    return (_a = promotedBreaks.find((promotion) => promotion.id === categoryId)) === null || _a === void 0 ? void 0 : _a.id;
};
export const convertInventoryDataQueryParamsToString = (inventoryDataQueryParams, durations, monthsQueryParam) => {
    const { guests, parkCode, sort, numberOfBedrooms, holidayType, accommodationSubType, accommodationType, from, size, priceType, grade, dogFriendly, baseGradeCode, resId, } = inventoryDataQueryParams;
    const params = new URLSearchParams();
    if (monthsQueryParam) {
        params.append('month', monthsQueryParam);
    }
    if (durations) {
        params.append('duration', durations.join(','));
    }
    if (guests === null || guests === void 0 ? void 0 : guests.adults) {
        params.append('adults', guests === null || guests === void 0 ? void 0 : guests.adults.toString());
    }
    if (guests === null || guests === void 0 ? void 0 : guests.children) {
        params.append('children', guests === null || guests === void 0 ? void 0 : guests.children.toString());
    }
    if (guests === null || guests === void 0 ? void 0 : guests.tots) {
        params.append('tots', guests === null || guests === void 0 ? void 0 : guests.tots.toString());
    }
    if (guests === null || guests === void 0 ? void 0 : guests.dogs) {
        params.append('dogs', guests === null || guests === void 0 ? void 0 : guests.dogs.toString());
    }
    if (sort) {
        params.append('sort', sort);
    }
    if (parkCode) {
        params.append('parkCode', parkCode.join(','));
    }
    if (numberOfBedrooms) {
        params.append('numberOfBedrooms', numberOfBedrooms);
    }
    if (holidayType) {
        params.append('holidayType', holidayType);
    }
    if (accommodationType) {
        params.append('accommodationType', accommodationType);
    }
    if (accommodationSubType) {
        params.append('accommodationSubType', accommodationSubType);
    }
    if (from) {
        params.append('from', from);
    }
    if (size) {
        params.append('size', size);
    }
    if (priceType) {
        params.append('priceType', priceType);
    }
    if (grade) {
        params.append('grade', grade);
    }
    if (dogFriendly) {
        params.append('dogFriendly', dogFriendly);
    }
    if (baseGradeCode) {
        params.append('baseGradeCode', baseGradeCode);
    }
    if (resId) {
        params.append('resId', `${resId}`);
    }
    return params.toString();
};
export const getAllAvailableDatesByDuration = ({ availability, selectedDurations, }) => {
    if (!availability || !availability.length)
        return undefined;
    return availability
        .filter(({ duration }) => selectedDurations.includes(duration.toString()))
        .flatMap(({ dates }) => dates);
};
export const convertDateToString = (date, newFormat = 'dd/MM/yyyy') => format(date, newFormat);
export const convertDateStringFormat = (dateString, currentFormat = 'dd/MM/yyyy', newFormat = 'yyyy-MM') => {
    const date = parse(dateString, currentFormat, new Date());
    return convertDateToString(date, newFormat);
};
export const formatDateRange = (startDate, endDate) => {
    if (startDate.getMonth() === endDate.getMonth()) {
        return `${format(startDate, 'EEE d')} - ${format(endDate, 'EEE d MMM yyyy')}`;
    }
    if (startDate.getFullYear() === endDate.getFullYear()) {
        return `${format(startDate, 'EEE d MMM')} - ${format(endDate, 'EEE d MMM yyyy')}`;
    }
    return `${format(startDate, 'EEE d MMM yyyy')} - ${format(endDate, 'EEE d MMM yyyy')}`;
};
export const getBreakDatesLabel = (arrivalDate, duration) => {
    const departureDate = addDays(arrivalDate, duration);
    return formatDateRange(arrivalDate, departureDate);
};
export const isValidDate = (d) => {
    return d instanceof Date && !isNaN(d.getTime());
};
export const isFutureDate = (d) => {
    if (!d) {
        return false;
    }
    const now = new Date();
    return isAfter(new Date(d), now);
};
export const formatMonthLabel = (date) => {
    if (!isValidDate(date))
        return undefined;
    return format(date, 'MMMM yyyy');
};
export const getTimeFromDateString = (date) => parse(date, 'dd/MM/yyyy', new Date()).getTime();
export const sortDateStringAsc = (a, b) => {
    if (getTimeFromDateString(a.date) < getTimeFromDateString(b.date))
        return -1;
    return 0;
};
export const createArrayWithLength = (length) => Array.from({ length }, (_, index) => index);
export const getIsDeposable = (arrivalDate) => isAfter(arrivalDate, addWeeks(new Date(), 10));
export const getFirstDeposableDate = (fromDate, leadingPrices) => {
    var _a;
    const isFromDateDeposable = fromDate && getIsDeposable(fromDate);
    const deposableBreak = (_a = leadingPrices === null || leadingPrices === void 0 ? void 0 : leadingPrices.find((leadingPrices) => leadingPrices === null || leadingPrices === void 0 ? void 0 : leadingPrices.isDeposable)) === null || _a === void 0 ? void 0 : _a.date;
    return deposableBreak && (!fromDate || !isFromDateDeposable)
        ? parse(deposableBreak, 'dd/MM/yyyy', new Date())
        : null;
};
